import GeoJSON from 'ol/format/GeoJSON';

import type { Map as CadenzaMap } from 'cadenza/map/map';
import { MapProperty } from 'cadenza/map/map-property';
import { RoutingEvent } from 'cadenza/map/routing/routing-constants';
import type { EventBus } from 'cadenza/utils/event-bus';
import { getHitGeometries } from 'cadenza/api-client/workbook-map-api';
import { getDpi } from 'cadenza/map/map-utils';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';
import { AsyncRectangleSelectionInteraction } from 'cadenza/map/interactions/async-rectangle-selection-interaction';
import type { HitPolygon } from 'cadenza/api-client/dto/hit-polygon-dto';
import { FeatureProperty } from 'cadenza/map/feature-property';
import { parseWorkbookUrl } from 'cadenza/api-client/workbook-api';
import { backendSingleExecution } from 'cadenza/api-client/api';
import type { GeometryType } from 'cadenza/map/geom/geometry-type';

import type Polygon from 'ol/geom/Polygon';
import type Feature from 'ol/Feature';
import type Interaction from 'ol/interaction/Interaction';
import type { Geometry as OlGeometry, MultiPolygon } from 'ol/geom';
import type { MapBrowserEvent } from 'ol';

export class LayerPolygonAsAvoidanceZoneMode {

  readonly _map;
  readonly _routingEventBus;
  _previousPrimaryInteraction?: Interaction;

  constructor (map: CadenzaMap, routingEventBus: EventBus) {
    this._map = map;
    this._routingEventBus = routingEventBus;
  }

  toggleMode (isOn: boolean) {
    if (isOn) {
      this._previousPrimaryInteraction = this._map.get(MapProperty.PRIMARY_INTERACTION) as Interaction;
      this._map.set(
        MapProperty.PRIMARY_INTERACTION,
        new AddLayerPolygonAvoidanceZoneInteraction(this._map, this._routingEventBus));
    } else {
      this._map.set(MapProperty.PRIMARY_INTERACTION, this._previousPrimaryInteraction);
    }
  }

}

export class AddLayerPolygonAvoidanceZoneInteraction extends AsyncRectangleSelectionInteraction {

  _map;
  _eventBus;
  _singleExecGetHitGeometries;

  constructor (map: CadenzaMap, eventBus: EventBus) {
    super();
    this._map = map;
    this._eventBus = eventBus;
    this._singleExecGetHitGeometries = backendSingleExecution<HitPolygon[]>(getHitGeometries);
  }

  asyncSelect (mapBrowserEvent: MapBrowserEvent<UIEvent>, geometry: OlGeometry) {
    // ! because we are in a workbook context (see check in `createRoutingUIRoot`)
    const { workbookIdAndHash, viewId } = parseWorkbookUrl(location.href)!;
    const srsCode = this._map.getView().getProjection().getCode();
    const mapSize = this._map.getSize() as DimensionDto;
    assertNonNullable(mapSize, 'Map size cannot be nullable');
    const dpi = getDpi(this._map.get(MapProperty.HIDPI));

    return this._singleExecGetHitGeometries({
      workbookIdAndHash,
      viewId,
      srsCode,
      geometry,
      dpi,
      mapSize,
      acceptedGeometryTypes: [ 'POLYGON' ]
    }).then(hitPolygons => {
      if (hitPolygons.length) {
        this._eventBus.dispatchEvent(createEvent(hitPolygons));
      }
    }).catch(error => logger.error('Error getting layer polygons for click on map', error));
  }

}

function createEvent (polygons: HitPolygon[]) {
  return new CustomEvent(
    RoutingEvent.LAYER_POLYGON_ADDED_AS_AVOIDANCE_ZONE,
    { detail: { features: convertToOlFeatures(polygons) } }
  );
}

const geoJSON = new GeoJSON();

interface PolygonFeatureWithGeomType {
  feature: Feature<Polygon | MultiPolygon>;
  geometryType: GeometryType;
}

function convertToOlFeatures (hitPolygons: HitPolygon[]): PolygonFeatureWithGeomType[] {
  return hitPolygons.map(hitPolygon => {
    const feature = geoJSON.readFeature(hitPolygon.geometry) as Feature<Polygon | MultiPolygon>;
    feature.set(FeatureProperty.ID, hitPolygon.hash);
    const geometryType = feature.getGeometry()!.getType();
    return { feature, geometryType };
  });
}
